import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { env } from '@/env';

let appInsightsInstance: ApplicationInsights | null = null;
let reactPluginInstance: ReactPlugin | null = null;

interface AppInsightsInstance {
  appInsights: ApplicationInsights;
  reactPlugin: ReactPlugin;
}

/**
 * Initialize Application Insights
 * This should be called once at app startup
 */
export const initializeAppInsights = (): AppInsightsInstance => {
  if (appInsightsInstance && reactPluginInstance) {
    return { appInsights: appInsightsInstance, reactPlugin: reactPluginInstance };
  }

  const appInsightsConnectionString = env.VITE_APPINSIGHTS_CONNECTION_STRING;
  const isTelemetryEnabled = Boolean(appInsightsConnectionString);

  if (!isTelemetryEnabled) {
    /* eslint-disable-next-line no-console -- intentional startup notice when App Insights is off */
    console.warn('Application Insights connection string not found. Telemetry will not be sent.');
  }

  reactPluginInstance = new ReactPlugin();

  appInsightsInstance = new ApplicationInsights({
    config: {
      connectionString: appInsightsConnectionString ?? '',
      extensions: [reactPluginInstance],
      enableAutoRouteTracking: isTelemetryEnabled,
      disableFetchTracking: true,
      disableAjaxTracking: true,
      autoTrackPageVisitTime: false,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      correlationHeaderExcludedDomains: ['*.queue.core.windows.net'],
      enableUnhandledPromiseRejectionTracking: true,
      disableTelemetry: !isTelemetryEnabled,
      disableCookiesUsage: true,
    },
  });

  if (isTelemetryEnabled) {
    appInsightsInstance.addTelemetryInitializer((telemetryItem: ITelemetryItem) => {
      const { tags } = telemetryItem;
      if (tags) {
        tags['ai.cloud.role'] = 'DermOnCall.Patient.React';
      }
    });

    appInsightsInstance.loadAppInsights();
  }

  return { appInsights: appInsightsInstance, reactPlugin: reactPluginInstance };
};

const getAppInsights = (): AppInsightsInstance => {
  if (!appInsightsInstance || !reactPluginInstance) {
    throw new Error(
      'Application Insights not initialized. You must call initializeAppInsights() first.',
    );
  }

  return { appInsights: appInsightsInstance, reactPlugin: reactPluginInstance };
};

export const trackException = (error: Error, properties?: Record<string, unknown>) => {
  const { appInsights: ai } = getAppInsights();
  ai.trackException({ exception: error, properties });
};

export const setAuthenticatedUserContext = (userId: string, accountId?: string) => {
  const { appInsights: ai } = getAppInsights();
  ai.setAuthenticatedUserContext(userId, accountId, true);
};

export const clearAuthenticatedUserContext = () => {
  const { appInsights: ai } = getAppInsights();
  ai.clearAuthenticatedUserContext();
};

/**
 * Returns the current App Insights session ID, forwarding it as a request header so that the
 * server-side TelemetryInitializer can restore session_Id on backend telemetry rows.
 *
 * The session ID lives in sessionStorage when disableCookiesUsage is true. It is created lazily
 * on the first call to sessionManager.update(), which normally happens when the SDK sends its
 * first telemetry item. Calling update() here ensures the session is initialized even if this
 * is invoked before the first page view fires.
 *
 * Returns undefined when App Insights is not yet initialized or telemetry is disabled.
 */
export const getAppInsightsSessionId = (): string | undefined => {
  try {
    const { appInsights: ai } = getAppInsights();
    // Force session creation if it hasn't happened yet (safe to call repeatedly).
    ai.context?.sessionManager?.update?.();
    return ai.context?.getSessionId?.() ?? undefined;
  } catch {
    return undefined;
  }
};
